/* ========================================================================== */
/*  FILE: components/home/HomeHeroClient.tsx                                   */
/* ========================================================================== */

"use client";

import Image from "next/image";
import Link from "next/link";
import React, {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  ArrowRight,
  Building2,
  ChefHat,
  Clock,
  Compass,
  MapPin,
  QrCode,
  Search,
  Shield,
  ShoppingBag,
  Sparkles,
  Star,
  UtensilsCrossed,
  Zap,
} from "lucide-react";

type HeroImage = {
  src: string;
  alt: string;
  locationLabel?: string;
};

type LocationOption = {
  value: string;
  label: string;
};

export type HomeHeroClientProps = {
  title?: string;
  subtitle?: string;
  locations?: LocationOption[];
  initialLocation?: string;
  heroImages?: HeroImage[];
  primaryCtaHref?: string;
  primaryCtaLabel?: string;
  secondaryCtaHref?: string;
  secondaryCtaLabel?: string;
  onSearch?: (payload: { query: string; location: string }) => void;
  searchPlaceholder?: string;
  showQuickStats?: boolean;
  stats?: Array<{ label: string; value: string }>;
};

function cn(...classes: Array<string | undefined | null | false>) {
  return classes.filter(Boolean).join(" ");
}

function useReducedMotion(): boolean {
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const onChange = () => setReduced(!!mq.matches);
    onChange();
    mq.addEventListener?.("change", onChange);
    return () => mq.removeEventListener?.("change", onChange);
  }, []);
  return reduced;
}

function safeClampIndex(index: number, length: number) {
  if (length <= 0) return 0;
  return ((index % length) + length) % length;
}

export default function HomeHeroClient(props: HomeHeroClientProps) {
  const reducedMotion = useReducedMotion();
  const searchId = useId();

  const heroImages: HeroImage[] = useMemo(
    () =>
      props.heroImages ?? [
        {
          src: "https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?q=80&w=2070&auto=format&fit=crop",
          alt: "Elegant restaurant interior",
          locationLabel: "Lilongwe",
        },
        {
          src: "https://images.unsplash.com/photo-1565299624946-b28f40a0ae38?q=80&w=1981&auto=format&fit=crop",
          alt: "Delicious cuisine",
          locationLabel: "Blantyre",
        },
        {
          src: "https://images.unsplash.com/photo-1555396273-367ea4eb4db5?q=80&w=2074&auto=format&fit=crop",
          alt: "Happy diners enjoying a meal",
          locationLabel: "Zomba",
        },
      ],
    [props.heroImages]
  );

  const locations: LocationOption[] = useMemo(
    () =>
      props.locations ?? [
        { value: "Lilongwe", label: "Lilongwe" },
        { value: "Blantyre", label: "Blantyre" },
        { value: "Mzuzu", label: "Mzuzu" },
        { value: "Zomba", label: "Zomba" },
        { value: "Near You", label: "Near you" },
        { value: "Malawi", label: "All Malawi" },
      ],
    [props.locations]
  );

  const [activeIndex, setActiveIndex] = useState(0);
  const [query, setQuery] = useState("");
  const [location, setLocation] = useState(props.initialLocation ?? "Lilongwe");
  const [geoLabel, setGeoLabel] = useState<string | null>(null);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);

  const intervalRef = useRef<number | null>(null);
  const lastInteractionRef = useRef<number>(0);

  const title = props.title ?? "Discover & Enjoy";
  const subtitle =
    props.subtitle ??
    "Browse, order ahead, book tables, track orders, and pay easily — all from your phone.";
  const primaryCtaHref = props.primaryCtaHref ?? "/vendors";
  const primaryCtaLabel = props.primaryCtaLabel ?? "Browse Restaurants";
  const secondaryCtaHref = props.secondaryCtaHref ?? "/vendors";
  const secondaryCtaLabel = props.secondaryCtaLabel ?? "Scan QR to Order Now";

  const stats =
    props.stats ?? [
      { value: "50+", label: "Restaurants" },
      { value: "25K+", label: "Happy Diners" },
      { value: "4.9", label: "Average Rating" },
      { value: "24/7", label: "Support" },
    ];

  const stopAutoPlay = useCallback(() => {
    setIsAutoPlaying(false);
    if (intervalRef.current) {
      window.clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  const startAutoPlay = useCallback(() => {
    if (reducedMotion) return;
    if (heroImages.length <= 1) return;
    if (intervalRef.current) return;

    intervalRef.current = window.setInterval(() => {
      const now = Date.now();
      if (now - lastInteractionRef.current < 1800) return;
      setActiveIndex((i) => safeClampIndex(i + 1, heroImages.length));
    }, 5200);

    setIsAutoPlaying(true);
  }, [heroImages.length, reducedMotion]);

  useEffect(() => {
    if (reducedMotion) return;
    startAutoPlay();
    return () => stopAutoPlay();
  }, [reducedMotion, startAutoPlay, stopAutoPlay]);

  useEffect(() => {
    if (typeof navigator === "undefined") return;
    if (!navigator.geolocation) return;

    navigator.geolocation.getCurrentPosition(
      () => setGeoLabel("Near you"),
      () => setGeoLabel("Malawi"),
      { maximumAge: 60_000, timeout: 4_000 }
    );
  }, []);

  const onNext = useCallback(() => {
    lastInteractionRef.current = Date.now();
    stopAutoPlay();
    setActiveIndex((i) => safeClampIndex(i + 1, heroImages.length));
  }, [heroImages.length, stopAutoPlay]);

  const onPrev = useCallback(() => {
    lastInteractionRef.current = Date.now();
    stopAutoPlay();
    setActiveIndex((i) => safeClampIndex(i - 1, heroImages.length));
  }, [heroImages.length, stopAutoPlay]);

  const onSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      const payload = { query: query.trim(), location };
      props.onSearch?.(payload);

      if (!props.onSearch) {
        const qp = new URLSearchParams();
        if (payload.query) qp.set("q", payload.query);
        if (payload.location) qp.set("location", payload.location);
        const href = `/vendors?${qp.toString()}`;
        window.location.assign(href);
      }
    },
    [location, props, query]
  );

  const active = heroImages[safeClampIndex(activeIndex, heroImages.length)];

  return (
    <section
      className="relative w-full overflow-hidden bg-gradient-to-br from-neutral-950 via-neutral-900 to-neutral-950"
      aria-label="IndeMenu hero"
      onMouseEnter={() => {
        lastInteractionRef.current = Date.now();
        stopAutoPlay();
      }}
      onMouseLeave={() => {
        lastInteractionRef.current = Date.now();
        if (!reducedMotion) startAutoPlay();
      }}
    >
      {/* Background media */}
      <div className="absolute inset-0">
        {heroImages.map((img, idx) => {
          const isActive = idx === safeClampIndex(activeIndex, heroImages.length);
          return (
            <div
              key={`${img.src}-${idx}`}
              className={cn(
                "absolute inset-0 transition-opacity duration-1000",
                isActive ? "opacity-100" : "opacity-0"
              )}
              aria-hidden={!isActive}
            >
              <Image
                src={img.src}
                alt={img.alt}
                fill
                priority={idx === 0}
                sizes="100vw"
                quality={88}
                className="object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/45 to-black/75" />
              <div className="absolute inset-0 bg-gradient-to-r from-black/40 via-transparent to-black/40" />
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_10%,rgba(251,191,36,0.18),transparent_55%)]" />
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_80%_35%,rgba(249,115,22,0.15),transparent_60%)]" />
            </div>
          );
        })}
      </div>

      {/* Top subtle highlight line */}
      <div className="pointer-events-none absolute left-0 right-0 top-0 h-px bg-gradient-to-r from-transparent via-white/25 to-transparent" />

      <div className="relative z-10 mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 pt-16 sm:pt-20 lg:pt-28 pb-16 lg:pb-20">
        {/* Badge */}
        <div className="mx-auto mb-6 flex w-fit items-center gap-2 rounded-full border border-white/15 bg-white/10 px-4 py-2 backdrop-blur-xl shadow-[0_0_0_1px_rgba(255,255,255,0.06)]">
          <Sparkles className="h-4 w-4 text-amber-300" />
          <span className="text-xs sm:text-sm font-semibold text-white/90 tracking-wide">
            Curated dining experiences in Malawi
          </span>
          <span className="ml-1 hidden sm:inline-flex items-center gap-1 rounded-full bg-white/10 px-2 py-0.5 text-[11px] text-white/80">
            <MapPin className="h-3.5 w-3.5" />
            {active?.locationLabel ?? geoLabel ?? "Malawi"}
          </span>
        </div>

        <div className="mx-auto max-w-4xl text-center">
          <h1 className="text-balance text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-extrabold leading-[1.05] text-white">
            <span className="block">{title}</span>
            <span className="mt-3 block bg-gradient-to-r from-amber-300 via-orange-400 to-amber-200 bg-clip-text text-transparent">
              Malawi&apos;s Best Restaurants, Cafes & Bars
            </span>
          </h1>

          <p className="mt-6 text-pretty text-base sm:text-lg md:text-xl text-white/85 leading-relaxed">
            {subtitle}
          </p>

          {/* Search */}
          <div className="mt-10">
            <form
              onSubmit={onSubmit}
              aria-labelledby={searchId}
              className="mx-auto max-w-3xl"
            >
              <div
                className={cn(
                  "relative overflow-hidden rounded-2xl border border-white/15 bg-white/10 backdrop-blur-xl shadow-2xl",
                  "shadow-black/30"
                )}
              >
                <div className="pointer-events-none absolute inset-0 bg-gradient-to-r from-amber-500/10 via-white/0 to-orange-500/10" />
                <div className="relative flex flex-col md:flex-row">
                  <div className="flex-1 px-4 sm:px-5 py-4">
                    <div className="flex items-center gap-3">
                      <Search className="h-5 w-5 text-white/70" />
                      <label id={searchId} className="sr-only">
                        Search restaurants
                      </label>
                      <input
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        placeholder={
                          props.searchPlaceholder ??
                          "Search restaurants, cuisines, or dishes…"
                        }
                        className={cn(
                          "w-full bg-transparent border-none outline-none text-white placeholder:text-white/55",
                          "text-sm sm:text-base"
                        )}
                        autoComplete="off"
                        inputMode="search"
                      />
                    </div>

                    <div className="mt-3 hidden md:flex items-center gap-3 text-xs text-white/70">
                      <span className="inline-flex items-center gap-1">
                        <ChefHat className="h-4 w-4" />
                        Local favorites
                      </span>
                      <span className="inline-flex items-center gap-1">
                        <Clock className="h-4 w-4" />
                        Order ahead
                      </span>
                      <span className="inline-flex items-center gap-1">
                        <Shield className="h-4 w-4" />
                        Secure payments
                      </span>
                    </div>
                  </div>

                  <div className="flex items-center justify-between gap-3 border-t border-white/12 bg-white/5 px-4 sm:px-5 py-3 md:border-t-0 md:border-l md:px-4">
                    <div className="flex items-center gap-2">
                      <MapPin className="h-5 w-5 text-white/70" />
                      <select
                        value={location}
                        onChange={(e) => setLocation(e.target.value)}
                        className="bg-transparent text-white/90 text-sm sm:text-base outline-none"
                        aria-label="Select location"
                      >
                        {locations.map((opt) => (
                          <option
                            key={opt.value}
                            value={opt.value}
                            className="bg-neutral-900 text-white"
                          >
                            {opt.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    <button
                      type="submit"
                      className={cn(
                        "inline-flex items-center justify-center gap-2 rounded-xl px-5 py-3 font-bold text-white",
                        "bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600",
                        "transition-transform duration-200 active:scale-[0.98]"
                      )}
                    >
                      <span className="text-sm sm:text-base">Search</span>
                      <ArrowRight className="h-5 w-5" />
                    </button>
                  </div>
                </div>
              </div>

              <div className="mt-4 flex flex-wrap items-center justify-center gap-2 text-xs text-white/65">
                <span className="inline-flex items-center gap-1 rounded-full border border-white/10 bg-white/5 px-3 py-1.5">
                  <Star className="h-3.5 w-3.5 text-amber-300" />
                  Top-rated places
                </span>
                <span className="inline-flex items-center gap-1 rounded-full border border-white/10 bg-white/5 px-3 py-1.5">
                  <UtensilsCrossed className="h-3.5 w-3.5 text-orange-300" />
                  Browse menus
                </span>
                <span className="inline-flex items-center gap-1 rounded-full border border-white/10 bg-white/5 px-3 py-1.5">
                  <Zap className="h-3.5 w-3.5 text-amber-200" />
                  Fast checkout
                </span>
                <span className="inline-flex items-center gap-1 rounded-full border border-white/10 bg-white/5 px-3 py-1.5">
                  <Compass className="h-3.5 w-3.5 text-white/80" />
                  Nearby discovery
                </span>
              </div>
            </form>
          </div>

          {/* CTA buttons */}
          <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              href={primaryCtaHref}
              className={cn(
                "group inline-flex w-full sm:w-auto items-center justify-center gap-3 rounded-2xl px-8 py-4",
                "bg-gradient-to-r from-amber-500 to-orange-500 text-white font-extrabold shadow-2xl shadow-black/25",
                "hover:from-amber-600 hover:to-orange-600 transition-transform duration-200 hover:scale-[1.01] active:scale-[0.98]"
              )}
            >
              <ShoppingBag className="h-5 w-5" />
              <span className="text-base sm:text-lg">{primaryCtaLabel}</span>
              <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
            </Link>

            <Link
              href={secondaryCtaHref}
              className={cn(
                "group inline-flex w-full sm:w-auto items-center justify-center gap-3 rounded-2xl px-8 py-4",
                "bg-white/10 text-white font-extrabold border-2 border-white/25 backdrop-blur-xl",
                "hover:bg-white/15 hover:border-white/35 transition-transform duration-200 hover:scale-[1.01] active:scale-[0.98]"
              )}
            >
              <QrCode className="h-5 w-5" />
              <span className="text-base sm:text-lg">{secondaryCtaLabel}</span>
            </Link>
          </div>

          {/* Micro trust row */}
          <div className="mt-8 flex flex-wrap items-center justify-center gap-3 text-xs sm:text-sm text-white/75">
            <span className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-4 py-2">
              <Shield className="h-4 w-4 text-amber-200" />
              Bank-level security
            </span>
            <span className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-4 py-2">
              <Building2 className="h-4 w-4 text-white/80" />
              Verified partners
            </span>
            <span className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-4 py-2">
              <ChefHat className="h-4 w-4 text-orange-200" />
              Fresh menus
            </span>
          </div>

          {/* Stats */}
          {props.showQuickStats !== false && (
            <div className="mt-12 grid grid-cols-2 sm:grid-cols-4 gap-4 sm:gap-5">
              {stats.map((s, i) => (
                <div
                  key={`${s.label}-${i}`}
                  className={cn(
                    "rounded-2xl border border-white/12 bg-white/7 backdrop-blur-xl px-5 py-5",
                    "shadow-[0_0_0_1px_rgba(255,255,255,0.04)]"
                  )}
                >
                  <div className="text-2xl sm:text-3xl font-extrabold text-white">
                    {s.value}
                  </div>
                  <div className="mt-1 text-xs sm:text-sm text-white/75">
                    {s.label}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Controls */}
          <div className="mt-10 flex items-center justify-center gap-3">
            <button
              type="button"
              onClick={onPrev}
              className={cn(
                "rounded-full border border-white/12 bg-white/10 px-4 py-2 text-white/90 backdrop-blur-xl",
                "hover:bg-white/15 transition-colors"
              )}
              aria-label="Previous hero image"
            >
              ←
            </button>

            <div className="flex items-center gap-2">
              {heroImages.map((_, idx) => {
                const isActive = idx === safeClampIndex(activeIndex, heroImages.length);
                return (
                  <button
                    key={`dot-${idx}`}
                    type="button"
                    onClick={() => {
                      lastInteractionRef.current = Date.now();
                      stopAutoPlay();
                      setActiveIndex(idx);
                    }}
                    className={cn(
                      "h-2.5 rounded-full transition-all",
                      isActive ? "w-10 bg-amber-400" : "w-2.5 bg-white/35 hover:bg-white/55"
                    )}
                    aria-label={`Go to slide ${idx + 1}`}
                    aria-pressed={isActive}
                  />
                );
              })}
            </div>

            <button
              type="button"
              onClick={onNext}
              className={cn(
                "rounded-full border border-white/12 bg-white/10 px-4 py-2 text-white/90 backdrop-blur-xl",
                "hover:bg-white/15 transition-colors"
              )}
              aria-label="Next hero image"
            >
              →
            </button>

            <span className="ml-2 hidden sm:inline text-xs text-white/60">
              {reducedMotion ? "Reduced motion" : isAutoPlaying ? "Auto-playing" : "Paused"}
            </span>
          </div>

          {/* Active slide meta */}
          <p className="mt-3 text-xs text-white/55">
            {active?.alt}
          </p>
        </div>
      </div>

      {/* Bottom fade */}
      <div className="pointer-events-none absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-white/0 via-white/0 to-transparent" />
    </section>
  );
}

